import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

/**
 * Shared layout for legal / compliance pages.
 * Renders a hero band, a sticky table of contents, and the page body.
 */
export default function LegalPage({
  eyebrow,
  title,
  lastUpdated,
  intro,
  sections,
  testId,
}) {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1" data-testid={testId}>
        {/* Hero band */}
        <section className="relative bg-slate-50/70 border-b border-slate-200">
          <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
          <div className="relative mx-auto max-w-5xl px-6 lg:px-10 py-16 lg:py-24">
            <Link
              to="/"
              data-testid="legal-back-link"
              className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeft size={14} /> Back to PS GLOBAL SERIES
            </Link>
            <div className="eyebrow mt-8">{eyebrow}</div>
            <h1
              data-testid="legal-page-title"
              className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900"
            >
              {title}
            </h1>
            <p className="mt-6 text-sm uppercase tracking-[0.18em] font-semibold text-slate-500">
              Last updated · {lastUpdated}
            </p>
            {intro && (
              <p className="mt-8 max-w-3xl text-[17px] text-slate-600 leading-relaxed">
                {intro}
              </p>
            )}
          </div>
        </section>

        {/* Content + TOC */}
        <section className="mx-auto max-w-5xl px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start">
            <div className="eyebrow text-slate-500">Contents</div>
            <ul className="mt-5 space-y-2.5 text-sm border-l border-slate-200">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`toc-${s.id}`}
                    className="group flex items-start gap-2 -ml-px border-l-2 border-transparent hover:border-slate-900 pl-4 py-1 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-400 group-hover:text-slate-700 mt-0.5">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="leading-snug">{s.heading}</span>
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          <article className="lg:col-span-9 space-y-14">
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} data-testid={`section-${s.id}`} className="scroll-mt-28">
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-[#B45309] font-semibold tracking-tight text-lg">
                    {String(i + 1).padStart(2, "0")}.
                  </span>
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
                    {s.heading}
                  </h2>
                </div>
                <div className="mt-5 space-y-5 text-[15px] leading-relaxed text-slate-700">
                  {s.body}
                </div>
              </section>
            ))}

            <div className="pt-10 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-sm text-slate-500">
                Questions about this document? Email{" "}
                <a
                  href="mailto:info@psglobalseries.com"
                  className="text-slate-900 font-semibold hover:text-[#B45309]"
                >
                  info@psglobalseries.com
                </a>
                .
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-1 text-sm font-semibold text-[#B45309] hover:text-[#92400E]"
              >
                Return to homepage <ChevronRight size={15} />
              </Link>
            </div>
          </article>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ---- Helpers used by individual legal pages ----
export function P({ children }) {
  return <p>{children}</p>;
}

export function UL({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-2 marker:text-[#B45309]">
      {items.map((it, i) => (
        <li key={typeof it === "string" ? `${i}-${it.slice(0, 40)}` : i}>{it}</li>
      ))}
    </ul>
  );
}

export function Strong({ children }) {
  return <span className="font-semibold text-slate-900">{children}</span>;
}
